<template>
  <div class="w-full max-h-screen bg-background flex flex-col relative">
    <page-header title="Scan Customer Return" backTo="goods-in" />

    <main class="p-6 bg-background flex-1 overflow-y-scroll">
      <div class="max-w-3xl">
        <div class="w-full bg-white p-5 rounded shadow-md relative">
          <div
            v-show="isBusy"
            class="absolute inset-0 bg-faint-white flex items-center justify-center z-20"
          >
            <Spinner :color="'text-black'" :size="10" />
          </div>

          <div class="flex items-center mb-5">
            <h3 class="uppercase text-lg font-semibold text-primary tracking-wide">
              Return Label
            </h3>
            <hr class="flex-1 border-primary mx-4" />
          </div>

          <div class="w-full">
            <label class="uppercase font-semibold text-sm">
              Scan the Label, or Enter the Label Code or Return Number
            </label>
            <div class="flex">
              <input
                ref="scanInput"
                v-model="receiptToken"
                class="bge-input bge-input-spacing w-full rounded-l"
                type="text"
                @keydown.enter="scan"
                data-testid="receipt-token"
              />
              <button
                @click="scan"
                class="rounded-r bg-blue-200 hover:bg-blue-300 border border-blue-400 hover:border-blue-500 p-2 hover:shadow active:shadow-inner transition-colors duration-300 ease-in-out"
                data-testid="scan"
              >
                <Icon iconType="search" />
              </button>
            </div>
            <p v-if="scanError" class="text-red-600 text-sm mt-1" data-testid="scan-error">
              {{ scanError }}
            </p>
          </div>
        </div>

        <div class="w-full bg-white p-5 rounded shadow-md mt-6" v-if="scanned">
          <div class="flex items-center mb-5">
            <h3 class="uppercase text-lg font-semibold text-primary tracking-wide">
              Expected Return
            </h3>
            <hr class="flex-1 border-primary mx-4" />
          </div>

          <div class="flex flex-wrap -mx-2">
            <div class="w-full lg:w-1/2 px-2 mb-4">
              <label class="uppercase font-semibold text-sm">Return Reference</label>
              <p class="text-lg font-bold" data-testid="scanned-return">
                {{ scanned.return_reference }}
              </p>
            </div>
            <div class="w-full lg:w-1/2 px-2 mb-4">
              <label class="uppercase font-semibold text-sm">Ticket Reference</label>
              <p class="text-lg" data-testid="scanned-ticket">
                {{ scanned.ticket_reference }}
              </p>
            </div>
            <div class="w-full lg:w-1/2 px-2 mb-4" v-if="scanned.warranty_reference">
              <label class="uppercase font-semibold text-sm">Warranty Reference</label>
              <p class="text-lg">{{ scanned.warranty_reference }}</p>
            </div>
            <div class="w-full lg:w-1/2 px-2 mb-4">
              <label class="uppercase font-semibold text-sm">Customer</label>
              <p>{{ scanned.customer_name }}</p>
            </div>
            <div class="w-full lg:w-1/2 px-2 mb-4">
              <label class="uppercase font-semibold text-sm">Product</label>
              <p>{{ scanned.product_code }} {{ scanned.product_name }}</p>
            </div>
            <div class="w-full lg:w-1/2 px-2 mb-4">
              <label class="uppercase font-semibold text-sm">Serial Number</label>
              <p>{{ scanned.item_serial_number || "(Not supplied)" }}</p>
            </div>
          </div>

          <div
            v-if="scanned.requires_product_review"
            class="bg-yellow-200 border border-yellow-500 rounded-lg py-3 px-4 text-yellow-900 mb-4"
            data-testid="product-review-warning"
          >
            The customer's product details could not be confirmed. Please check the item
            on arrival.
          </div>

          <div
            v-if="scanned.already_received"
            class="bg-blue-200 border border-blue-500 rounded-lg py-3 px-4 text-blue-900"
            data-testid="already-received"
          >
            This return has already been confirmed as received.
          </div>
        </div>

        <div
          class="w-full bg-white p-5 rounded shadow-md mt-6"
          v-if="scanned && !scanned.already_received"
        >
          <div class="flex items-center mb-5">
            <h3 class="uppercase text-lg font-semibold text-primary tracking-wide">
              Confirm Receipt
            </h3>
            <hr class="flex-1 border-primary mx-4" />
          </div>

          <div class="flex flex-wrap -mx-2">
            <div class="w-full lg:w-1/2 px-2 mb-4">
              <label class="uppercase font-semibold text-sm">Received Date</label>
              <Calendar
                class="w-full"
                v-model="confirmation.received_at"
                dateFormat="dd/mm/yy"
                :showIcon="true"
              />
            </div>
            <div class="w-full lg:w-1/2 px-2 mb-4">
              <label class="uppercase font-semibold text-sm">
                Delivery or Consignment Number
              </label>
              <input
                v-model="confirmation.consignment_number"
                class="bge-input bge-input-spacing w-full rounded"
                type="text"
                data-testid="consignment-number"
              />
            </div>
            <div class="w-full px-2 mb-4">
              <label class="uppercase font-semibold text-sm">Notes</label>
              <Textarea
                v-model="confirmation.notes"
                class="bge-input bge-input-spacing w-full rounded"
                :autoResize="false"
                rows="3"
              />
            </div>
            <div class="w-full px-2">
              <label class="uppercase font-semibold text-sm">Received By</label>
              <input
                :value="receivedByUser"
                readonly
                class="bge-input bge-input-spacing w-full rounded text-gray-500"
                type="text"
              />
            </div>
          </div>

          <div class="flex justify-end mt-5">
            <AppButton
              color="green"
              label="Confirm Received"
              :icon="true"
              @click="confirmReceived"
            >
              <template v-slot:icon>
                <Icon iconType="package-import" class="mr-2" />
              </template>
            </AppButton>
          </div>
        </div>

        <div
          class="w-full bg-white p-5 rounded shadow-md mt-6"
          v-if="confirmed"
          data-testid="confirmed-panel"
        >
          <div class="flex items-center mb-5">
            <h3 class="uppercase text-lg font-semibold text-primary tracking-wide">
              Received
            </h3>
            <hr class="flex-1 border-primary mx-4" />
          </div>
          <p>
            Return {{ confirmed.return_reference }} is now
            {{ confirmed.return_status }} and the ticket has been cleared from Items to
            Collect.
          </p>
        </div>
      </div>
    </main>
  </div>
</template>

<script>
import PageHeader from "@/components/PageHeader.vue";
import Spinner from "@/components/Spinner.vue";
import Icon from "@/components/Icon.vue";
import AppButton from "@/components/Button.vue";
import notificationMixin from "@/mixins/notificationMixin";
import ReturnFormLinkService from "@/services/returnFormLinkService";

export default {
  name: "ScanReturnLabel",
  mixins: [notificationMixin],
  components: {
    PageHeader,
    Spinner,
    Icon,
    AppButton,
  },
  data() {
    return {
      isBusy: false,
      receiptToken: "",
      scanned: null,
      scanError: "",
      confirmed: null,
      confirmation: {
        received_at: new Date(),
        consignment_number: "",
        notes: "",
      },
    };
  },
  mounted() {
    this.focusScanInput();
  },
  computed: {
    receivedByUser() {
      const user = this.$store.state.user;
      if (user && (user.first_name || user.last_name)) {
        return `${user.first_name || ""} ${user.last_name || ""}`.trim();
      }
      return this.$store.state.userEmail || "";
    },
  },
  methods: {
    focusScanInput() {
      if (this.$refs.scanInput) this.$refs.scanInput.focus();
    },
    async scan() {
      const token = String(this.receiptToken || "").trim();
      if (!token) {
        return;
      }

      this.isBusy = true;
      this.scanError = "";
      this.scanned = null;
      this.confirmed = null;

      try {
        this.scanned = await ReturnFormLinkService.scanReturnReceipt(token);
      } catch (error) {
        this.scanError =
          error && error.status === 404
            ? "No return found for that label code or return number."
            : "Could not check this label" +
              (error && error.data && error.data.message ? `: ${error.data.message}` : ".");
      } finally {
        this.isBusy = false;
      }
    },
    async confirmReceived() {
      const confirmedAction = await this.confirmAction({
        message: "Confirm that this return has been received?",
        header: "Confirm Receipt",
      });

      if (!confirmedAction) {
        return;
      }

      this.isBusy = true;
      try {
        this.confirmed = await ReturnFormLinkService.confirmReturnReceived(
          String(this.receiptToken).trim(),
          {
            consignment_number: this.confirmation.consignment_number || "",
            notes: this.confirmation.notes || "",
            received_at: this.confirmation.received_at
              ? this.$moment(this.confirmation.received_at).unix()
              : undefined,
          }
        );
        this.scanned = { ...this.scanned, already_received: true };
        this.notifySuccess("Return confirmed as received.");
      } catch (error) {
        this.notifyError("Could not confirm this return.");
      } finally {
        this.isBusy = false;
      }
    },
  },
};
</script>
